import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewChecked, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import flatpickr from 'flatpickr';
import { Spanish } from 'flatpickr/dist/l10n/es.js';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relative">
      <button (click)="toggleDateDropdown()" 
              class="bg-zaxis-dark border border-zaxis-border px-4 py-2 text-[11px] font-mono tracking-widest uppercase text-white focus:outline-none focus:border-zaxis-accent cursor-pointer flex items-center gap-2 hover:bg-white/5 transition-colors min-w-[200px] justify-between rounded-none">
        <div class="flex items-center gap-2">
          <i class="ph ph-calendar text-sm text-zaxis-accent"></i>
          <span>{{ timeRangeLabel }}</span>
        </div>
        <span class="text-[10px] text-gray-500 font-mono">▼</span>
      </button>

      <!-- Backdrop -->
      <div *ngIf="showDateDropdown" class="fixed inset-0 z-40" (click)="closeDateDropdown()"></div>

      <!-- Popover Content -->
      <div *ngIf="showDateDropdown" class="absolute right-0 top-full mt-2 bg-zaxis-card border border-zaxis-border shadow-pixel z-50 flex overflow-hidden animate-fade-in rounded-none">
        
        <!-- Presets Sidebar -->
        <div class="flex flex-col border-r border-zaxis-border bg-zaxis-dark min-w-[160px] py-2">
          <p class="px-4 py-2 text-[10px] text-zaxis-accent font-mono font-bold uppercase tracking-widest">Rangos Rápidos</p>
          <button (click)="selectPreset('15m')" [ngClass]="selectedTimeRange === '15m' ? 'bg-zaxis-accent text-black font-bold' : 'text-gray-400 hover:bg-white/5'" class="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-widest transition-colors rounded-none">Últimos 15 min</button>
          <button (click)="selectPreset('30m')" [ngClass]="selectedTimeRange === '30m' ? 'bg-zaxis-accent text-black font-bold' : 'text-gray-400 hover:bg-white/5'" class="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-widest transition-colors rounded-none">Últimos 30 min</button>
          <button (click)="selectPreset('1h')"  [ngClass]="selectedTimeRange === '1h' ? 'bg-zaxis-accent text-black font-bold' : 'text-gray-400 hover:bg-white/5'" class="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-widest transition-colors rounded-none">Última 1 hora</button>
          <button (click)="selectPreset('24h')" [ngClass]="selectedTimeRange === '24h' ? 'bg-zaxis-accent text-black font-bold' : 'text-gray-400 hover:bg-white/5'" class="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-widest transition-colors rounded-none">Últimas 24 horas</button>
          <button (click)="selectPreset('7d')"  [ngClass]="selectedTimeRange === '7d' ? 'bg-zaxis-accent text-black font-bold' : 'text-gray-400 hover:bg-white/5'" class="px-4 py-2.5 text-left text-[11px] font-mono uppercase tracking-widest transition-colors rounded-none">Últimos 7 días</button>
        </div>

        <!-- Calendar Area -->
        <div class="p-4 bg-zaxis-card">
          <p class="text-[10px] text-zaxis-accent mb-3 font-mono font-bold uppercase tracking-widest">Rango Personalizado</p>
          <!-- Flatpickr inline container -->
          <div #inlineDatePicker></div>
        </div>
      </div>
    </div>
  `
})
export class DateRangePickerComponent implements AfterViewChecked, OnDestroy {
  @Input() selectedTimeRange: string = '7d';
  @Input() customStartDate: string = '';
  @Input() customEndDate: string = '';
  @Output() rangeChange = new EventEmitter<{ range: string, start?: string, end?: string }>();

  @ViewChild('inlineDatePicker', { static: false }) inlineDatePickerRef?: ElementRef;
  
  showDateDropdown = false;
  private fpInstance: any;

  get timeRangeLabel(): string {
    switch (this.selectedTimeRange) {
      case '15m': return 'Últimos 15 min';
      case '30m': return 'Últimos 30 min';
      case '1h': return 'Última 1 hora';
      case '24h': return 'Últimas 24 horas';
      case '7d': return 'Últimos 7 días';
      case 'custom':
        if (this.customStartDate && this.customEndDate) {
          return `${new Date(this.customStartDate).toLocaleDateString()} - ${new Date(this.customEndDate).toLocaleDateString()}`;
        }
        return 'Personalizado';
      default: return 'Seleccionar...';
    }
  }

  toggleDateDropdown() {
    this.showDateDropdown = !this.showDateDropdown;
  }

  closeDateDropdown() {
    this.showDateDropdown = false;
  }

  selectPreset(range: string) {
    this.selectedTimeRange = range;
    this.customStartDate = '';
    this.customEndDate = '';
    this.closeDateDropdown();
    this.rangeChange.emit({ range: this.selectedTimeRange });
  }

  ngAfterViewChecked() {
    if (this.showDateDropdown && this.inlineDatePickerRef && !this.fpInstance) {
      this.fpInstance = flatpickr(this.inlineDatePickerRef.nativeElement, {
        mode: 'range',
        inline: true,
        locale: Spanish,
        maxDate: 'today',
        dateFormat: 'Y-m-d',
        defaultDate: [this.customStartDate, this.customEndDate].filter(d => d),
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.selectedTimeRange = 'custom';
            this.customStartDate = selectedDates[0].toISOString();
            this.customEndDate = selectedDates[1].toISOString();
            this.closeDateDropdown();
            this.rangeChange.emit({ 
              range: 'custom', 
              start: this.customStartDate, 
              end: this.customEndDate 
            });
          }
        }
      });
    } else if (!this.showDateDropdown && this.fpInstance) {
      this.fpInstance.destroy();
      this.fpInstance = null;
    }
  }

  ngOnDestroy() {
    if (this.fpInstance) {
      this.fpInstance.destroy();
    }
  }
}
